import React, { useState, useEffect, useLayoutEffect, useMemo, useReducer, useCallback, useRef } from "react";
import Head from "next/head";
import dynamic from 'next/dynamic';
import ChannelSearch from '../video-lab/ChannelSearch';
import {
  chlupStoreReducer, initializeChlupStore,
  filterCategoricalChannels
} from "./ChlupStore";
import {
  ChnlListingPageFeed,
  IPackage
} from "./chnlListingPage.props";
import {
  KeyValuePair,
  Choice,
  PACKAGE_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fireCustomLinkClickGA,
  fireGa4ProductImpression
} from "@directv-digital/dds.services.analytics-service";
import {
  getDmaCookies
} from "@directv-digital/dds.services.cookie-service";
// Generated by Copilot- Start - 2026-04-01 20:31:38
import { useGlobalConfigs } from "@directv-digital/dds.server.application-context";
// Generated by Copilot- End - 2026-04-01 20:31:38
import { zIndexMap } from "./utils/zIndexMap";
import { buildLocalRsnChannel, isChannelInGenre, isNationalForGenre } from "./utils/utilityFunctions";
import { LOCAL_DEFAULT_PACKAGES, RSN_DEFAULT_PACKAGES } from "./utils/fallbackData";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isLocalChannelExcludedFromSignature, getSignaturePackagesForLocalChannel } from "lib/service/channels/get-local-national-dma-eligible-channels";
import { County } from "@directv-digital/dds.apis.county-lookup";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";

import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvModal from '@directv-digital/dds.ui.dtv-modal';
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

/** Components (Above fold) ---------------------------- */
import { PackageComparisonGrid } from './PackageComparisonGrid';
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import { IconBanner } from "./IconBanner";
import MyFreeDtvBanner from "./components/MyFreeDtvBanner";
import TabTray from "src/components/video-lab/tab-tray";

/** Below Fold components
 * - rendered per condition not on load
 * - low priority
 * */
const ChnlListingPageFooter = dynamic(() => import("./components/Footer"), { ssr: false });
const ScrollToTop = dynamic(() => import('@directv-digital/dds.ui.scroll-to-top'), { ssr: false });
const RsnChannelModal = dynamic(() => import("./components/RsnChannelModal"), { ssr: false });
const ChannelModal = dynamic(() => import("./components/ChannelModal"), { ssr: false });

function sendOnLoadAnalytics(packages: IPackage[]) {
  const packageDataGA = packages
    ?.filter(row => row?.googleAnalytics?.id)
    ?.map((row, index) => ({
      id: row?.googleAnalytics.id,
      position: index + 1
    }));
  if (packageDataGA?.length > 0) {
    fireGa4ProductImpression(packageDataGA);
  }
};

function safeRequestIdleCallback(callback, timeout?: number) {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, timeout || 0);
  }
}

// useLayoutEffect on client (fires before paint), useEffect on server (never runs during SSR).
// Avoids React's "useLayoutEffect does nothing on the server" warning.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function ChnlListingPage(props: ChnlListingPageFeed) {
  try {
    const componentId = props?.options?.id || "Chnl-Listing-Page";
    const {
      channelData,
      config,
      content,
      searchObj
    } = props;
    const {
      theming,
      genrePackTriggers = [],
      heading,
      toggleBtn,
      subheading,
      subheadingLegal,
      zipcodeModuleConfig,
      myFreeDirectvBanner,
      searchConfig,
      pkgIndicatorIcons,
      gridInitViewMode,
      gridSortOptions,
      pkgIndicatorTooltipPrefixText,
      addOnPromoLogoInfo,
      categoryFooterCtas,
      internetChnlsPDFLink,
      satelliteChnlsPDFLink,
      satelliteCta,
      channelModalContent,
      localChnlBadgeTooltip,
      rsnChnlBadgeTooltip,
      embeddedSvods,
      isEmbeddedSvodsEnabled,
      genreNationals,
      myFreeConfig,
      isSatelliteChlup,
      pageHeading,
      genreMapping,
      genreToPackageMapping
    } = content;
    const {
      channels,
      bannerCategories,
      bannerPackages,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      searchCategories,
      gridCategories,
      gridPackages,
      myFreeDtvChannels = []
    } = channelData;
    // Generated by Copilot- Start - 2026-04-01 20:31:38
    const globalConfigs = useGlobalConfigs();
    const { genreLocalChannelMapping } = globalConfigs ?? {};
    const signatureExcludedLocalChannels = globalConfigs?.signatureExcludedLocalChannels;
    // Generated by Copilot- End - 2026-04-01 20:31:38
    const chnlSrcLC = config?.chnlSrc?.toLowerCase();
    const isSatQP = config?.chnlSrc?.toLowerCase() === "satelliteclup";
    const localRsnApiOptions = useMemo(() => ({
      satellite: isSatQP,
      stream: !isSatQP
    }), []);

    /** ------------------------------------------------------------------------------- */
    /** Toggle Button state ------------------------------------------------------------*/
    // !!!!!Must be first thing done
    // --> toggle state directly affects which views channel set to compute/display first

    // Chlup Default Tab to Genre if user comes from genre specific URL's
    let defaultIndex = toggleBtn?.toggles?.findIndex(x => x.selected) || 0;
    if (genrePackTriggers?.length) {
      // eslint-disable-next-line array-callback-return
      genrePackTriggers?.some(triggerUrl => {
        if (typeof window !== "undefined" && document.referrer?.includes(triggerUrl)) {
          toggleBtn.toggles[0].selected = false;
          toggleBtn.toggles[1].selected = true;
          defaultIndex = 1;
          return true;
        }
        toggleBtn.toggles[0].selected = true;
        toggleBtn.toggles[1].selected = false;
        defaultIndex = 0;
      });
    }
    if (config?.viewQueryParam === 'genre') {
      const genreIdx = toggleBtn?.toggles?.findIndex(t => t.label?.toLowerCase().includes('genre'));
      if (genreIdx > -1) {
        defaultIndex = genreIdx;
      }
    }

    const [selectedToggle, setSelectedToggle] =
    useState(isSatelliteChlup ? "PACKAGES" : toggleBtn?.toggles[defaultIndex]?.label?.toUpperCase())
    const [tabDefaultIndex, setTabDefaultIndex] = useState(defaultIndex);
    const [tabKey, setTabKey] = useState(0);
    const viewQPCorrectionApplied = useRef(false);
    const curToggleIndex =
    useMemo(() => toggleBtn?.toggles?.findIndex(x => x.label?.toUpperCase() === selectedToggle), [selectedToggle]);

    // viewQPCorrectionApplied.current is a run-once guard: without it, setSelectedToggle would change
    // selectedToggle, re-trigger this effect (selectedToggle is in deps), and loop infinitely.
    useIsomorphicLayoutEffect(() => {
      if (viewQPCorrectionApplied.current) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const genreToggle = toggleBtn?.toggles?.find(t => t.label?.toLowerCase().includes('genre'));
      const genreToggleIdx = genreToggle ? toggleBtn.toggles.indexOf(genreToggle) : -1;
      if (params.get('view')?.toLowerCase() === 'genre' && genreToggle) {
        viewQPCorrectionApplied.current = true;
        const genreLabel = genreToggle.label?.toUpperCase();
        if (genreLabel && selectedToggle !== genreLabel) {
          setSelectedToggle(genreLabel);
          setTabDefaultIndex(genreToggleIdx);
          setTabKey(k => k + 1);
          dispatchChlupState({
            type: "UPDATE_COMPONENT_VIEW",
            data: { selectedToggle: genreLabel }
          });
        }
      }
    }, [toggleBtn, selectedToggle]);

    // Non-GP view toggle
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleToggleBtnChange = (e, label) => {
      if (label?.toUpperCase() !== selectedToggle) {
        setSelectedToggle(label?.toUpperCase());
        dispatchChlupState({
          type: "UPDATE_COMPONENT_VIEW",
          data: {
            selectedToggle: label?.toUpperCase()
          }
        })
      }
    }

    // GP view toggle
    const handleTabTrayBtnChange = index => {
      if(index !== curToggleIndex) {
        setSelectedToggle(toggleBtn?.toggles?.[index]?.label?.toUpperCase());
        dispatchChlupState({
          type: "UPDATE_COMPONENT_VIEW",
          data: {
            selectedToggle: toggleBtn?.toggles?.[index]?.label?.toUpperCase()
          }
        })
      }
    }
    /** ------------------------------------------------------------------------------- */

    /** Channels store -----------------------------------------------------------------*/
    const gridCategoriesAZ = [
      {
        key: 'INCLUDED_CHANNELS',
        value: 'Included channels'
      },
      {
        key: 'ADD_ON_CHANNELS',
        value: 'Add-on channels'
      }
    ];
    // Add MyFree Category to A-Z View
    if(myFreeConfig?.myFreeCategory) {
      gridCategoriesAZ.push(myFreeConfig?.myFreeCategory);
    }
    // Add EmbeddedSVODs Accordian Data to A-Z View only if feature is enabled
    if (isEmbeddedSvodsEnabled) {
      gridCategoriesAZ.unshift({
        key: 'INCLUDED_SUBSCRIPTIONS',
        value: 'Included subscriptions'
      } );
    }

    const [chlupStore, dispatchChlupState] = useReducer(
      chlupStoreReducer,
      {
        channels,
        embeddedSvods,
        selectedToggle,
        bannerCategories,
        bannerPackages,
        gridCategoriesAZ,
        gridCategories,
        gridPackages,
        myFreeKey: myFreeConfig?.myFreeCategory?.key,
        currentDma: getDmaCookies()?.currentDma || getDmaCookies()?.nielsenDma,
        genreNationalMapping: genreNationals?.genreNationalMapping,
        genreMapping
      },
      initializeChlupStore
    );
    const {
      cmprGridChannelsAZ,
      cmprGridChannelsNum,
      cmprGridChannelsCategorical,
      bannerChannels,
      searchChoices,
      needsUpdate,
      activeView,
      searchChoicesSvods
    } = chlupStore;
    /** State mutating the displayed channels */
    const [selectedCategories, setSelectedCategories] = useState<KeyValuePair[]>([]);
    const [searchChannelList, setSearchChannelList] = useState<Choice[]>([]);
    const searchActive = useMemo(() => searchChannelList?.length > 0, [searchChannelList]);
    const [showMoreCategories, setShowMoreCategories] = useState([]);

    /** Local/Rsn API state ------------------------------------------------------------*/
    const [isLoadingLocalRsn, setIsLoadingLocalRsn] = useState(true);
    const [fetchedZipcodeModuleData, setFetchedZipcodeModuleData] = useState(null);

    useEffect(() => {
      safeRequestIdleCallback(() => {
        if (chlupStore?.activeView === "categories") {
          sendOnLoadAnalytics(bannerPackages);
        } else if (chlupStore?.activeView === "packages") {
          sendOnLoadAnalytics(gridPackages);
        }
      }, 50);
    }, []);

    /** -------------------------------------------------------------------------------
   *  Channel Data sets
   *  */
    const filteredGridChannelsAZ = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsAZ)?.length === 0) {
        return cmprGridChannelsAZ;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod")  },
        cmprGridChannelsAZ
      );
    }, [cmprGridChannelsAZ, searchChannelList]);

    const filteredEmbeddedSvods = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(embeddedSvods)?.length === 0) {
        return embeddedSvods;
      }

      const searchTagsvalues = searchChannelList.filter(item => item.type === "svod").flatMap(item => item.value);
      const embeddedSvodArr = embeddedSvods.filter(item => searchTagsvalues.includes(item.name));
      return embeddedSvodArr
    }, [embeddedSvods, searchChannelList]);

    const filteredGridChannelsCategorical = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsCategorical)?.length === 0) {
        return cmprGridChannelsCategorical;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod") },
        cmprGridChannelsCategorical
      )
    }, [cmprGridChannelsCategorical, searchChannelList]);

    const filteredGridChannelsNum = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsNum)?.length === 0) {
        return cmprGridChannelsNum;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod") },
        cmprGridChannelsNum
      );
    }, [cmprGridChannelsNum, searchChannelList]);

    /** Icon Banner w/Genre = FALSE only */
    // ^^ Remove this comment if categories enabled for Packages Grid
    const bannerSelectedCategories = useMemo(() => bannerCategories, [selectedCategories]);

    const filteredBannerChannels = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(bannerChannels)?.length === 0) {
        return bannerChannels;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList },
        bannerChannels
      );
    }, [bannerChannels, searchChannelList]);

    /** >> When chlupStore is initialized or local/rsn channels are added:
   *    - ONLY the current page's channel set(s) are calculated to avoid unnecessary computation (~5-20ms)
   *    - needsUpdate flag is set true for other page(s)
   *    - below useEffects is triggered
   *    - idle callbacks created to update when main thread open
   *    - on component view switch, (when main thread open) to update channel sets
   */
    useEffect(() => {
      if (chlupStore?.needsUpdate?.banner === true) {
        if (activeView === 'categories') {
          dispatchChlupState({ type: 'REFRESH_BANNER' })
        } else {
          safeRequestIdleCallback(() => dispatchChlupState({ type: 'REFRESH_BANNER' }), 50);
        }
      } else if (chlupStore?.needsUpdate?.grid === true) {
        if (activeView === 'packages') {
          dispatchChlupState({ type: 'REFRESH_PKGS_GRID' })
        } else {
          safeRequestIdleCallback(() => dispatchChlupState({ type: 'REFRESH_PKGS_GRID' }), 50);
        }
      }
    }, [needsUpdate, activeView]);

    /** -------------------------------------------------------------------------------
   *  Zipcode Module Callback functions
   *  */
    const onZipcodeUpdateClick = useCallback(
      (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => {
        setIsLoadingLocalRsn(true); // Set state for Icon Banner UI
        setFetchedZipcodeModuleData(() => ({
          zipcode,
          counties,
          rsnFee,
          localRsnChnls,
          county
        }))
      }
      , []
    );

    /** Builds data DIRECTLY from LocalRsnChnlService */
    const buildLocalRsnChannels = useCallback((channels: LocalRsnApiResponse, currentDma: string, nielsenDma: string) => {
      const { localChannels = [], rsnChannels = [] } = channels;
      if (localChannels?.length === 0 && rsnChannels?.length === 0) {
        console.info("[ChannelListingPage] buildLocalRsnChannels: local and rsn channels not found")
      }

      let localrsnUUID = 0;
      const genreMappingKeys = Object.keys(genreMapping);

      const tempLocalChannels = [];
      let tempRsnChannels = [];

      const getGenrePackages = channel => {
        const genrePackages = [];
        const isNational = {};
        for (const genre of genreMappingKeys) {
          if (isChannelInGenre(channel, genreLocalChannelMapping, genre, currentDma, nielsenDma)) {
            genrePackages.push(genreToPackageMapping[genre]);
          } else if(isNationalForGenre(channel, genre, currentDma, nielsenDma, genreNationals?.genreNationalMapping)){
            isNational[genre] = true;
            // Also set isNational for the banner category key (package name without ATT_TV_ prefix, lowercased)
            const packageName = genreToPackageMapping[genre];
            const bannerKey = packageName?.replace(/^ATT_TV_/i, '')?.toLowerCase();
            if (bannerKey && bannerKey !== genre) {
              isNational[bannerKey] = true;
            }
            genrePackages.push(genreToPackageMapping[genre]);
          }
        }
        return {
          genrePackages,
          isNational
        };
      }

      const getIndicators = (category, packageNames: string[]) => {
        if(category?.toUpperCase() === "RSN") {
          return  gridPackages?.map(p => {
            const pkgArr = p?.id.split('TV_');
            const pkgId = pkgArr[pkgArr.length - 1];
            if (packageNames?.some(inP => inP === pkgId)) {
              return PACKAGE_INDICATORS.IN;
            }
            return null;
          })
        }
        return gridPackages?.map(p => {
          const pkgArr = p?.id.split('TV_');
          const pkgId = pkgArr[pkgArr.length - 1];
          if (packageNames?.some(inP => inP.includes(pkgId))) {
            return PACKAGE_INDICATORS.IN;
          }
          return null;
        });
      }
      for (const channel of localChannels) {
      // As of 5/15 hotfix local api doesn't send any package info.
      // Conactenate LOCAL_DEFAULT_PACKAGES packages and genre packages
      // Generated by Copilot- Start - 2026-04-23 09:00:00
      // Per-DMA signature-package exclusion (CMS config: signatureExcludedLocalChannels).
      // Supports both blanket DMA exclusion (all signature pkgs) and per-package narrow rules.
        const channelNetwork = channel?.network || channel?.name || channel?.channelNameLabel;
        const signaturePackages = getSignaturePackagesForLocalChannel(channelNetwork, currentDma, LOCAL_DEFAULT_PACKAGES, signatureExcludedLocalChannels);
        const { genrePackages, isNational } = getGenrePackages(channel);
        const chnlPackages = [
          ...signaturePackages,
          ...(genrePackages?.length ? genrePackages : [])
        ];

        // If a local channel ends up with NO matching packages at all (excluded from
        // every signature package AND not mapped into any genre pack for this DMA),
        // skip injecting it entirely — otherwise it renders as an empty row in the
        // "Included channels" grid (e.g. Telemundo in DMAs 820/635/819/801 when the
        // CMS config blanket-excludes it from signature).
        if (chnlPackages.length === 0) {
          continue;
        }
        // Generated by Copilot- End - 2026-04-23 09:00:00
        const indicators = getIndicators(channel?.categoryId, signaturePackages);
        tempLocalChannels.push({
          ...buildLocalRsnChannel(channel, localrsnUUID++, chnlPackages, indicators),
          chnlPackages,
          displayText: channel?.callSign,
          isNational,
          signatureExcluded: signaturePackages.length === 0
        });
      }
      for (const channel of rsnChannels) {
        const indicators = getIndicators(channel?.categoryId, channel?.packageNames);

        // Read packageNames param from channel object, add genre packages if any
        // Fallback to RSN_DEFAULT_PACKAGES in case of API failure
        const { genrePackages, isNational } = getGenrePackages(channel);
        const chnlPackages = [
          ...(channel?.packageNames?.length ? channel.packageNames : RSN_DEFAULT_PACKAGES),
          ...(genrePackages?.length ? genrePackages : [])
        ]
        tempRsnChannels.push({
          ...buildLocalRsnChannel(channel, localrsnUUID++, chnlPackages, indicators),
          chnlPackages,
          displayText: channel?.callSign,
          isNational
        });
      }

      // eslint-disable-next-line array-callback-return
      tempRsnChannels = tempRsnChannels.filter(channel => {
        for (const indicator of channel?.indicators || []) {
          if (indicator === 1) {
            return channel;
          }
        }
      })

      // Set state for Icon Banner UI
      setIsLoadingLocalRsn(false);
      // Filter local/rsn channels into the main channel set
      dispatchChlupState({
        type: "ADD_LOCAL_RSN_CHANNELS",
        data: {
          local: tempLocalChannels,
          rsn: tempRsnChannels,
          currentDma: currentDma || nielsenDma,
          genreNationalMapping: genreNationals?.genreNationalMapping
        }
      })
    }, [gridPackages, genreMapping, genreLocalChannelMapping, genreToPackageMapping]);

    const onCountySearchDone = useCallback(
      (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => {
        setFetchedZipcodeModuleData(() => ({
          zipcode,
          counties,
          rsnFee,
          localRsnChnls,
          county
        }))
      }
      , []
    );

    /** -------------------------------------------------------------------------------
   * Search and Category Handling
   */
    const updateShowMoreCategories = categories => {
      const data = [];
      categories.forEach((val: any) => data[val] = false);
      setShowMoreCategories(data);
    };

    const updateSearch = (searchChannels: Choice[]) => {
      setSearchChannelList(searchChannels);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const multiSelectInputEvents = {
      handleTagsChange: (selectedChannel: Choice[], selectedCategories: KeyValuePair[]) => {
        updateShowMoreCategories(selectedCategories);
        setSelectedCategories(selectedCategories);
        setSearchChannelList(selectedChannel);
      },
      // on category filter
      updateCategories: (categories: KeyValuePair[]) => {
        updateShowMoreCategories(categories?.map(c => c?.key));
        setSelectedCategories(categories);
      },
      // on search item selection
      onSelectSearchListItem: (searchChannels: Choice[]) => {
        setSearchChannelList(searchChannels);
      }
    };

    useEffect(() => {
      setShowMore(selectedCategories?.length > 0);
    }, [selectedCategories]);

    /** -------------------------------------------------------------------------------
   * Model state + Open/Close Events
   */
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [clickedChannelData, setClickedChannelData] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const handleModalClose = () => {
      setShowChannelModal(false);
      setClickedChannelData(null);
    }

    const openChannelModal = channel => {
      setClickedChannelData(() => (channel));
      setShowChannelModal(true);
    };

    return (
    <DtvBox>
      {isSatelliteChlup ? pageHeading?.content && <DtvText
        variant="h2"
        component={pageHeading?.component || "h1"}
        content={pageHeading?.content || "Channel lineups subject to change based on agreements with programming providers"}
        sx={{
          padding: {
 xs: "10px 10px 10px 10px",
sm: "10px 20px 10px 20px",
md: "10px 20px 10px 20px"
},
          backgroundColor: "#ffffff",
          color: colorPalette.black.main,
          textAlign: "center",
          fontSize: {
 xs: "20px",
sm: "32px",
md: "36px"
},
          lineHeight: {
 xs: "28.8px",
sm: "36px",
md: "48.4px"
},
          letterSpacing: {
 xs: "-0.48px",
sm: "-0.6px",
md: "-0.88px"
},
          fontWeight: {
 xs: 400,
sm: 500
},
          height: {
 xs: "80px",
sm: "110px",
md: "130px"
}
        }}
      /> : <></>}
    <DtvBox
      id={`${componentId}-root`}
      sx={{
        background: {
          xs: theming?.pageBackgroundUrl ?
            `url("${theming?.pageBackgroundUrl}?imwidth=300") top / cover no-repeat${theming?.pageBackgroundColor ? `, ${theming?.pageBackgroundColor}` : ''}` :
            theming?.pageBackgroundColor,
          sm: theming?.pageBackgroundUrl ?
            `url("${theming?.pageBackgroundUrl}") top / cover no-repeat${theming?.pageBackgroundColor ? `, ${theming?.pageBackgroundColor}` : ''}` :
            theming?.pageBackgroundColor
        },
        backgroundSize: {
          xs: "100% 221px",
          sm: "100% 434px",
          md: " 100% 810px"
        }
      }}>
        <Head>
          {theming?.pageBackgroundUrl &&
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={theming?.pageBackgroundUrl}
              media="(min-width: 601px)"
            />
          } {theming?.pageBackgroundUrl &&
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={theming?.pageBackgroundUrl}
              media="(max-width: 600px)"
            />
          }
        </Head>

        <DtvBox sx={{
          position: "relative",
          zIndex: zIndexMap.pageHeadingTextContainer,
          padding: {
            xs: "24px 20px 0px 20px",
            sm: "54px 20px 0px 20px",
            md: "90px 20px 0px 20px"
          }
        }}>
          {/** Heading */}
          <DtvText
            variant="h2"
            component={heading?.component || "h1"}
            content={heading?.content || "Stream all your favorite channels"}
            sx={{
              color: theming?.headerTextColor,
              textAlign: "center",
              fontSize: {
 xs: "24px",
sm: "30px",
md: "44px"
},
              lineHeight: {
 xs: "28.8px",
sm: "36px",
md: "48.4px"
},
              letterSpacing: {
 xs: "-0.48px",
sm: "-0.6px",
md: "-0.88px"
},
              fontWeight: {
 xs: 400,
sm: 500
},
              px: {
 xs: "12px",
sm: "32.5px",
md: "50px"
}
            }}
          />
          {/* Toggle Button */}
          {!isSatelliteChlup && (toggleBtn?.enabled) && (
            <DtvBox
              sx={{
                display: "flex",
                justifyContent: "center",
                py: {
 xs: "13px",
sm: "16px",
md: "32px"
},
                width: "100%",
                "& > div": {
                  width: "fit-content",
                  maxWidth: "522px",
                  height: {
 xs: "36px",
sm: "48px",
md: "48px"
}
                },
                "& > div > div": {
                  width: { xs: "100%" }
                },
                "& > div > div > button": {
                  width: "100% !important",
                  height: "100%",
                  fontSize: {
 xs: "14px",
sm: "16px"
},
                  lineHeight: {
 xs: "18.2px",
sm: "20.8px"
},
                  color: colorPalette.white.main
                }
            }}>
              <TabTray
                key={tabKey > 0 ? tabKey : undefined}
                id="Channel-Category"
                variant="tray"
                tabs={toggleBtn?.toggles}
                defaultSelectedTab={tabDefaultIndex}
                handleChange={handleTabTrayBtnChange}
                ariaLabel={`Show Channel By ${toggleBtn?.toggles?.[curToggleIndex]?.label?.toLowerCase()}`}
                mode={theming?.toggleBtnMode}
                styles={{
                  width: {
 xs: "180px",
md: "250px"
},
                  minHeight: "46px"
                }}
              />
            </DtvBox>
          )}
          {/** Zipcode Module */}
          <DtvBox
            sx={{
              maxWidth: {
                xs: "522px",
                sm: "100%"
              },
              margin: "0 auto",
              paddingBottom: {
                xs: "8px",
                sm: "31px",
                md: "27px"
              }
            }}>
            <ZipcodeModule
              id="Chlup-Zipcode-Module"
              mode={theming?.zipCodeModuleMode}
              onSearchButtonClick={onZipcodeUpdateClick}
              onLocalRsnChnlsConfirmed={buildLocalRsnChannels}
              onCacheConfirmed={onCountySearchDone}
              localRsnApiOptions={localRsnApiOptions}
              ciConfig={zipcodeModuleConfig}
            />
          </DtvBox>
          <DtvBox
            sx={{
              maxWidth: "522px",
              margin: "0 auto"
            }}
          >
            <ChannelSearch
              id={`${componentId}-channel-search`}
              {...searchObj}
              channels={[...searchChoices, ...searchChoicesSvods]}
              theming={theming?.chipsMode}
              updateSearchEvents={updateSearch}
              type="chnlup"
            />
          </DtvBox>
          {/* <MultiChnlSearch
            id={componentId}
            events={multiSelectInputEvents}
            choices={searchChoices?.length ? searchChoices : []}
            choicesSvod={searchChoicesSvods?.length ? searchChoicesSvods: []}
            channelCategories={searchCategories}
            searchConfig={searchConfig}
            genreEnabled={config?.displayGenrePacks}
            initValue={""}
            selectedToggle={selectedToggle}
            theming={theming?.chipsMode}
            isSatelliteChlup={isSatelliteChlup}
          />         */}
          {/* MyFreeDirectv Banner in category tab */}
          {selectedToggle === "CATEGORY" && myFreeDirectvBanner && (
            <DtvBox sx={{
              p: {
                xs: "20px 0px 16px 0px",
                sm: "20px 57.5px 24px 57.5px",
                md: "32px 146px 24px 146px"
              },
              "& > div": {
                pr: {
 xs: "0px !important",
sm: "20px !important"
}
              }
            }}>
              <MyFreeDtvBanner
                {...myFreeDirectvBanner}
                channels={myFreeDtvChannels}
                theming={theming?.categoriesTab?.myFreeDtvBanner}
              />
            </DtvBox>
          )}
          {/* Local Rsn legal + MyFreeDirectv Banner in genre tab */}
          {(selectedToggle === 'GENRE PACKS' && (subheading?.content || subheadingLegal?.content)) && (
            <>
              <DtvBox sx={{
                width: "100%",
                display: "flex",
flexDirection: "column",
justifyContent: "center",
                px: {
                  xs: "14px",
                  sm: "90px",
                  md: "196px"
                }
              }}>
                {subheading?.content && (
                <DtvText
                  component="p"
                  variant="subhead1medium"
                  content={subheading?.content}
                  sx={{
                    color: theming?.subheadingTextColor,
                    textAlign: "center",
                    fontSize: {
 xs: "16px !important",
md: "18px !important"
},
                    fontFamily: 'roboto-regular',
                    fontWeight: '400 !important',
                    mb: {
 xs: "10px",
sm: "12px",
md: "20px"
}
                  }}
                />
                )}
                {/* Local Rsn Legal in genre tab */}
                {subheadingLegal?.content && (
                  <DtvText
                    component="p"
                    variant="smalltext"
                    content={subheadingLegal?.content}
                    sx={{
                      color: theming?.subheadingLegalTextColor,
                      textAlign: "center",
                      fontSize: { xs: "14px !important" },
                      fontFamily: 'roboto-regular',
                      fontWeight: '400 !important'
                    }}
                  />
                )}
              </DtvBox>
              {/* MyFreeDirectvBanner in Genre Tab */}
              {myFreeDirectvBanner && (
              <DtvBox sx={{
                p: {
                  xs: "20px 14px",
                  sm: "27px 90px",
                  md: "36px 196px"
                },
                "& > div": {
                  pr: {
 xs: "0px !important",
sm: "20px !important"
}
                }
              }}>
                <MyFreeDtvBanner
                  {...myFreeDirectvBanner}
                  channels={myFreeDtvChannels}
                  theming={theming?.categoriesTab?.myFreeDtvBanner}
                />
              </DtvBox>
              )}
            </>
          )}
        </DtvBox>

        {(activeView === 'packages' || isSatelliteChlup) ? (
            <PackageComparisonGrid
              channelsAZ={filteredGridChannelsAZ}
              channelsFilteredByNumber={filteredGridChannelsNum}
              channelsCategorical={filteredGridChannelsCategorical}
              myFreeDtvChannels={myFreeDtvChannels}
              categoriesAZ={gridCategoriesAZ}
              categoriesCategorical={gridCategories}
              initViewMode={gridInitViewMode}
              sortOptions={gridSortOptions}
              searchListActive={searchActive}
              packages={gridPackages}
              myFreeDirectvBanner={myFreeDirectvBanner}
              pkgIndicatorIcons={pkgIndicatorIcons}
              pkgIndicatorTooltipPrefixText={pkgIndicatorTooltipPrefixText}
              addOnPromoLogoInfo={addOnPromoLogoInfo}
              theming={theming?.packagesTab}
              noMatchPageMsg={searchConfig?.noResultsMsgPackagesGrid}
              embeddedSvods={filteredEmbeddedSvods}
              isEmbeddedSvodsEnabled={isEmbeddedSvodsEnabled}
              rsnTierLegalTextObj={content?.rsnTierLegalTextObj}
              myFreeConfig={myFreeConfig}
            />
        ) : (
          (activeView === 'categories' &&  !isSatelliteChlup) && (
            <DtvBox
              id={`${componentId}-channelsdiv`}
              sx={{
                "::-webkit-scrollbar": {
                  width: "4px"
                },
                "::-webkit-scrollbar-thumb": {
                  backgroundColor: "darkgrey",
                  borderRadius: "2px"
                },
                maxHeight: "100%",
                overflow: "hidden",
                pt: "0px",
mt: {
 xs: "24px",
sm: "24px",
md: "32px"
},
                ...(chlupStore?.channels?.length === searchChannelList.length &&
                    selectedCategories.length === 0 && !showMore
                ) ? {
                  position: "relative",
                  display: "block"
                } : {
                  overflowY: "scroll"
                }
              }}>
                <IconBanner
                  id={ `${componentId}-banner`}
                  bannerCategories={bannerSelectedCategories}
                  bannerChannels={filteredBannerChannels}
                  bannerPackages={bannerPackages}
                  isSearching={isLoadingLocalRsn}
                  showMoreData={showMoreCategories}
                  updateShowMoreCategories={data => setShowMoreCategories(data)}
                  openChannelModal={openChannelModal}
                  zipCodeModule={{
                    id: "Chlup-Zipcode-Module-My-Home-Team",
                    mode: theming?.zipCodeModuleMode,
                    size: "small",
                    position: "left",
                    onSearchButtonClick: onZipcodeUpdateClick,
                    onLocalRsnChnlsConfirmed: buildLocalRsnChannels,
                    localRsnApiOptions,
                    initData: fetchedZipcodeModuleData,
                    isConditional: true
                  }}
                  categoryFooterCtas={categoryFooterCtas}
                  localChnlBadgeTooltip={localChnlBadgeTooltip}
                  rsnChnlBadgeTooltip={rsnChnlBadgeTooltip}
                  internetChnlsPDFLink={selectedToggle === "CATEGORY" ? internetChnlsPDFLink : undefined}
                  theming={theming?.categoriesTab?.category}
                  noMatchPageMsg={searchConfig?.noResultsMsgCategoriesBanner}
                  genreNationals={genreNationals}
                  myFreeConfig={myFreeConfig}
                />
            </DtvBox>
          )
        )}

        {(activeView === 'packages' || isSatelliteChlup) && (
          <ChnlListingPageFooter
            id={`${componentId}-Footer`}
            internetChnlsPDFLink={internetChnlsPDFLink}
            satelliteChnlsPDFLink={satelliteChnlsPDFLink}
            isSatelliteChlup={isSatelliteChlup}
            satelliteCta={satelliteCta}
            theme={theming?.footerMode}
          />
        )}

        <ScrollToTop
          showAlways={false}
          backgroundColor="#FFF"
          color="#102641"
        />

        <DtvModal
          open={showChannelModal}
          handleClose={handleModalClose}
          modalStyle={{
            maxHeight: 'min-content'
          }}
          modalBodyStyle={{
            height: 'min-content',
            maxHeight: {
              xs: '90vh',
              sm: '80vh'
            }
          }}>
            <>
            {['rsn', 'local'].includes(clickedChannelData?.category?.toLowerCase()) ?
              <RsnChannelModal
                  channel={clickedChannelData}
                  packages={bannerPackages}
                  ctaForInternetInLocalRSN={channelModalContent?.ctaForInternetInLocalRSN}
                  ctaForSatelliteLocalRSN={channelModalContent?.ctaForSatelliteLocalRSN}
                  chnlSrcLC={chnlSrcLC}
                  rsnTiers={channelModalContent?.rsnTiers}
                  // legacy
                  rsnModalLegal={channelModalContent?.rsnModalLegal}
                /> :
              <ChannelModal
                  channel={clickedChannelData}
                  packages={bannerPackages}
                  internetPkgCta={channelModalContent?.internetPkgCta}
                  satellitePkgCta={channelModalContent?.satellitePkgCta}
                  myFreeDTVCTA={channelModalContent?.myFreeDTVCTA}
                  myFreeDTVPkgLabel={channelModalContent?.myFreeDTVPkgLabel}
                  chnlSrcLC={chnlSrcLC}
                />
            }
            </>
        </DtvModal>
    </DtvBox>
    </DtvBox>
    );
  } catch (error) {
    console.error("[ChannelListingPage] !!!!! FATAL ERROR !!!!! ", error);
    return <ErrorDisplay error={error} component="Channel Listing Page"/>;
  }
}
